import React, { useMemo, useState, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StageAverageChart = ({ data, stageColors = {}, defaultColors = [], dynamicColumns = [] }) => {
  // Ref para capturar a área do gráfico para exportação
  const chartAreaRef = useRef(null);
  
  // Estado para controle do tooltip de ajuda
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Estados para controle da exportação
  const [showExportModal, setShowExportModal] = useState(false);

  // Funções de exportação
  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    setShowExportModal(false);
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'stage-average-chart.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  const cancelExport = () => {
    setShowExportModal(false);
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Obter colunas de workflow do primeiro item
    const firstItem = data[0];
    const dynamicColumns = firstItem.workflowColumns || [];
    
    if (dynamicColumns.length === 0) return null;

    // Calcular tempo médio por etapa
    const stageAverages = dynamicColumns.map(stageName => {
      const stageDurations = [];
      
      data.forEach(item => {
        const stageIndex = dynamicColumns.indexOf(stageName);
        const currentDate = item.workflowDates[stageName];
        
        if (currentDate) {
          let nextDate;
          
          // Determinar a próxima data
          if (stageIndex < dynamicColumns.length - 1) {
            const nextColumn = dynamicColumns[stageIndex + 1];
            nextDate = item.workflowDates[nextColumn];
          } else {
            nextDate = item['Closed Date'];
          }
          
          if (nextDate) {
            const duration = Math.ceil((nextDate - currentDate) / (1000 * 60 * 60 * 24));
            if (duration >= 0) {
              stageDurations.push(duration);
            }
          }
        }
      });
      
      // Calcular média
      const average = stageDurations.length > 0 
        ? stageDurations.reduce((sum, duration) => sum + duration, 0) / stageDurations.length
        : 0;
      
      return {
        stage: stageName,
        average: Math.round(average * 10) / 10, // Arredondar para 1 casa decimal
        count: stageDurations.length
      };
    });

    // Usar as mesmas cores do breakdown (cores personalizadas ou padrão)
    const usedColumns = dynamicColumns.length > 0 ? dynamicColumns : stageAverages.map(item => item.stage);
    const colors = usedColumns.map((column, index) => {
      // Se há cores personalizadas para esta etapa, usar ela
      if (stageColors[column]) {
        return stageColors[column];
      }
      // Senão, usar cor padrão do array recebido ou fallback
      if (defaultColors.length > 0) {
        return defaultColors[index % defaultColors.length];
      }
      // Fallback para cores internas caso não receba defaultColors
      const fallbackColors = [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
        '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'
      ];
      return fallbackColors[index % fallbackColors.length];
    });

    return {
      labels: stageAverages.map(item => item.stage),
      datasets: [
        {
          label: 'Tempo médio (dias)',
          data: stageAverages.map(item => item.average),
          backgroundColor: stageAverages.map((item, index) => {
            // Encontrar a cor correta baseada na etapa
            const stageIndex = usedColumns.indexOf(item.stage);
            return colors[stageIndex !== -1 ? stageIndex : index];
          }),
          borderColor: stageAverages.map((item, index) => {
            // Encontrar a cor correta baseada na etapa
            const stageIndex = usedColumns.indexOf(item.stage);
            return colors[stageIndex !== -1 ? stageIndex : index];
          }),
          borderWidth: 1,
        }
      ],
      stageDetails: stageAverages
    };
  }, [data, stageColors, defaultColors, dynamicColumns]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Barras horizontais
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const stageDetails = chartData.stageDetails[context.dataIndex];
            return [
              `Tempo médio: ${context.parsed.x} dias`,
              `Baseado em ${stageDetails.count} item(ns)`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Tempo médio (dias)',
          font: {
            size: 13,
            weight: 600
          },
          color: '#374151'
        },
        beginAtZero: true,
        grid: {
          display: true,
          color: '#E5E7EB'
        },
        ticks: {
          color: '#374151',
          font: {
            size: 12,
            weight: 500
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Etapas do Processo',
          font: {
            size: 13,
            weight: 600
          },
          color: '#374151'
        },
        grid: {
          display: false
        },
        ticks: {
          color: '#374151',
          font: {
            size: 12,
            weight: 500
          }
        }
      },
    },
  };

  if (!chartData) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md">
        <div className="text-center py-8 text-gray-500">
          <p>Nenhum dado de etapas encontrado para análise.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto mt-8 p-6 bg-white rounded-xl shadow-md">
      {/* Cabeçalho com título e ícones */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-800">Tempo Médio por Etapa</h3>
          
          {/* Tooltip de Explicação ao lado do título */}
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="Como interpretar este gráfico"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            
            {/* Tooltip de Ajuda */}
            {showTooltip && (
              <div className="absolute left-0 top-full mt-2 w-96 bg-gray-800 text-white text-sm rounded-lg shadow-lg p-5 z-10">
                <div className="font-semibold mb-3 text-blue-200">📊 Como interpretar este gráfico:</div>
                <div className="space-y-2 text-xs">
                  <div className="border-l-2 border-blue-400 pl-3">
                    <div className="font-medium text-blue-200 mb-1">📊 Estrutura do gráfico:</div>
                    <div>• Cada barra horizontal = uma etapa do seu processo de trabalho</div>
                    <div>• Largura da barra = tempo médio que os itens ficam nesta etapa</div>
                    <div>• Baseado nos {data.length} itens selecionados no gráfico de breakdown acima</div>
                  </div>
                  
                  <div className="border-l-2 border-green-400 pl-3">
                    <div className="font-medium text-green-200 mb-1">⚡ Identificando gargalos:</div>
                    <div>• Barras mais longas = etapas que demoram mais tempo</div>
                    <div>• Compare as larguras para identificar onde focar melhorias</div>
                    <div>• Etapas com maior tempo médio são candidatas prioritárias para otimização</div>
                  </div>
                  
                  <div className="border-l-2 border-yellow-400 pl-3">
                    <div className="font-medium text-yellow-200 mb-1">💡 Dicas de uso:</div>
                    <div>• Passe o mouse sobre as barras para ver valores exatos</div>
                    <div>• Use junto com o gráfico de breakdown para análise completa</div>
                    <div>• Foque nas etapas com maior tempo para melhorar o fluxo geral</div>
                  </div>
                </div>
                <div className="absolute top-0 left-6 transform -translate-y-1 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Botão de Exportar */}
          <button
            onClick={handleExportClick}
            className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium"
            title="Exportar gráfico como imagem"
          >
            <span className="mr-2">📸</span>
            Exportar
          </button>
        </div>
      </div>
      
      <div ref={chartAreaRef} className="h-80 mb-6" style={{ background: '#fff', borderRadius: 8, padding: 16 }}>
        <Bar data={chartData} options={options} />
      </div>

      {/* Modal de Confirmação de Exportação */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">📸</span>
              <h3 className="text-lg font-semibold text-gray-900">Exportar Gráfico</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Deseja fazer o download do gráfico de Tempo Médio por Etapa como imagem PNG?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelExport}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
              >
                Fazer Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageAverageChart;
